/**
 * Order batch schemas.
 *
 * Stages 1–5 of the pipeline. Each batch progresses through these dates,
 * with optional gaps (pre-shipment and transit dates may be skipped).
 *
 * Status lifecycle: ACTIVE → CANCELLED / REPLACED.
 * Cancelled and replaced batches are excluded from the aggregate gate
 * and from the ordered-quantity sum.
 */

import { Type, type Static } from '@sinclair/typebox';
import { OrderBatchStatuses } from '../enums.js';
import { FIELD_LIMITS, NUMERIC_LIMITS } from '../constants.js';
import {
  IsoDateSchema,
  RowVersionField,
  SoftDeleteFields,
  TimestampFields,
  UuidSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Read shape
// ---------------------------------------------------------------------------

export const OrderBatchSchema = Type.Object({
  id: UuidSchema,
  projectItemId: UuidSchema,
  supplierId: UuidSchema,
  /** Captured at creation; preserved even if the supplier is renamed in the master. */
  supplierNameSnapshot: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX }),
  quantity: Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX }),

  // Stage 1
  orderPlacedDate: IsoDateSchema,
  expectedArrivalDate: IsoDateSchema,

  // Stages 2–5
  preShipmentInspectionDate: Type.Union([IsoDateSchema, Type.Null()]),
  inTransitDate: Type.Union([IsoDateSchema, Type.Null()]),
  arrivedDate: Type.Union([IsoDateSchema, Type.Null()]),
  inHouseInspectionDate: Type.Union([IsoDateSchema, Type.Null()]),

  // Lifecycle
  status: Type.Union(OrderBatchStatuses.map((s) => Type.Literal(s))),
  cancellationDate: Type.Union([IsoDateSchema, Type.Null()]),
  cancellationReason: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.CANCELLATION_REASON_MAX }),
    Type.Null(),
  ]),
  cancelledByUserId: Type.Union([UuidSchema, Type.Null()]),
  /** Forward pointer: when this batch was replaced, who's the replacement (optional). */
  replacedByBatchId: Type.Union([UuidSchema, Type.Null()]),
  /** Backward pointer: this batch replaces an earlier one (optional). */
  replacesBatchId: Type.Union([UuidSchema, Type.Null()]),

  // Provenance
  createdByUserId: UuidSchema,

  ...TimestampFields,
  ...SoftDeleteFields,
  ...RowVersionField,
});
export type OrderBatch = Static<typeof OrderBatchSchema>;

// ---------------------------------------------------------------------------
// Read shape with derived data
// ---------------------------------------------------------------------------

export const OrderBatchWithStatusSchema = Type.Composite([
  OrderBatchSchema,
  Type.Object({
    /** Highest stage number reached on this batch (1–5). 0 means none yet (shouldn't happen — stage 1 is implicit). */
    currentStageNumber: Type.Integer({ minimum: 1, maximum: 5 }),
    /** Days in the current stage. */
    daysInCurrentStage: Type.Integer({ minimum: 0 }),
    /** True when Expected Arrival is in the past and Arrived is not yet recorded (only meaningful for ACTIVE batches). */
    isOverdue: Type.Boolean(),
    /** Joined supplier object — useful when displaying batches without a separate lookup. */
    supplierName: Type.String(), // matches current master name (may differ from snapshot)
  }),
]);
export type OrderBatchWithStatus = Static<typeof OrderBatchWithStatusSchema>;

// ---------------------------------------------------------------------------
// Create / update inputs
// ---------------------------------------------------------------------------

export const OrderBatchCreateSchema = Type.Object({
  supplierId: UuidSchema,
  quantity: Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX }),
  orderPlacedDate: IsoDateSchema,
  expectedArrivalDate: IsoDateSchema,
});
export type OrderBatchCreate = Static<typeof OrderBatchCreateSchema>;


/**
 * Bulk creation — creates multiple ACTIVE batches in one request. Each row is
 * still persisted as an independent order batch against its own project item.
 */
export const OrderBatchBulkCreateItemSchema = Type.Composite([
  Type.Object({
    projectItemId: UuidSchema,
  }),
  OrderBatchCreateSchema,
]);
export type OrderBatchBulkCreateItem = Static<typeof OrderBatchBulkCreateItemSchema>;

export const OrderBatchBulkCreateSchema = Type.Object({
  batches: Type.Array(OrderBatchBulkCreateItemSchema, { minItems: 1, maxItems: 100 }),
});
export type OrderBatchBulkCreate = Static<typeof OrderBatchBulkCreateSchema>;

/**
 * Update of metadata + stage dates. Pipeline-stage sequence (each date ≥ prior) is
 * enforced server-side.
 */
export const OrderBatchUpdateSchema = Type.Object({
  supplierId: Type.Optional(UuidSchema),
  quantity: Type.Optional(Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX })),
  orderPlacedDate: Type.Optional(IsoDateSchema),
  expectedArrivalDate: Type.Optional(IsoDateSchema),
  preShipmentInspectionDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  inTransitDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  arrivedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  inHouseInspectionDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type OrderBatchUpdate = Static<typeof OrderBatchUpdateSchema>;

/**
 * Cancellation — Admin only.
 */
export const OrderBatchCancelSchema = Type.Object({
  cancellationReason: Type.String({
    minLength: 1,
    maxLength: FIELD_LIMITS.CANCELLATION_REASON_MAX,
  }),
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type OrderBatchCancel = Static<typeof OrderBatchCancelSchema>;

/**
 * Replacement — Admin only. Marks an existing batch as REPLACED and links the new batch.
 */
export const OrderBatchReplaceSchema = Type.Object({
  /** The new batch's creation data. */
  replacement: OrderBatchCreateSchema,
  cancellationReason: Type.String({
    minLength: 1,
    maxLength: FIELD_LIMITS.CANCELLATION_REASON_MAX,
  }),
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type OrderBatchReplace = Static<typeof OrderBatchReplaceSchema>;
